import type { AlignmentStatus } from '../../types/metro';
import { cn } from '../../lib/utils';

/**
 * Per-status chip styling — tinted pastel for inactive, fully filled for
 * active. Mirrors `_archive/map.html` lines 202-207 EXACTLY (these
 * specific hex tints don't appear elsewhere in the app and are
 * intentionally lighter than the popup-pill palette since
 * they need to read as "filter buttons" not "data labels").
 */
interface ChipPaint {
  readonly inactive: string;
  readonly active: string;
}

const PAINT: Record<AlignmentStatus, ChipPaint> = {
  shortage: {
    inactive: 'border-[#f0c0c7] bg-[#fef2f4] text-rose',
    active: 'border-rose bg-rose text-white',
  },
  // LUIS-fix-1: surplus chip switched from blue family to green family
  // per Luis's directive + his updated `_inbox/luis-fixes/map.html`
  // lines 169 & 172.
  surplus: {
    // A11y (WCAG 1.4.3): text-green2 on the light-green inactive tint is 4.37:1
    // → green-dark (5.60:1). Active fill (white on green2) is 4.78:1, unchanged.
    inactive: 'border-[#b3d9b8] bg-[#edf7f1] text-green-dark',
    active: 'border-green2 bg-green2 text-white',
  },
};

const LABEL: Record<AlignmentStatus, string> = {
  shortage: 'Shortage',
  surplus: 'Surplus',
};

/**
 * Per-status SHAPE icon shown when the chip is INACTIVE. Mirrors
 * `_inbox/luis-fixes/map.html` lines 519-538 (DEC-056: balanced
 * branch removed alongside the type narrowing):
 *
 *   shortage → downward triangle (severity going down)
 *   surplus  → upward triangle (going up)
 *
 * LUIS-fix-2: previously the chips rendered no icons at all. The
 * shape-↔-check toggle is a key part of Luis's spec — it tells the
 * user "this chip is a filter you can toggle, and right now it's off."
 */
function ShapeIcon({ status }: { status: AlignmentStatus }) {
  // DEC-056: AlignmentStatus is binary (shortage|surplus) — the
  // <circle> fallback that used to render for 'balanced' is gone.
  const path =
    status === 'shortage'
      ? 'M12 19l-7-7h14z'
      : 'M12 5l7 7H5z';
  return (
    <svg
      aria-hidden="true"
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="flex-shrink-0"
    >
      <path d={path} />
    </svg>
  );
}

/**
 * Check icon shown when the chip is ACTIVE. Single shape across all
 * three statuses — `currentColor` lets the active-paint's `text-white`
 * cascade through.
 */
function CheckIcon() {
  return (
    <svg
      aria-hidden="true"
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="flex-shrink-0"
    >
      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
    </svg>
  );
}

interface StatusChipsProps {
  /** Currently-active status filters. Multi-select (matches the prototype). */
  readonly selected: ReadonlySet<AlignmentStatus>;
  readonly onToggle: (status: AlignmentStatus) => void;
}

/**
 * Three-up status filter chips for the map's floating panel. Mirrors
 * prototype `.status-chips` block (lines 194-207) plus Luis's May 6
 * icon-swap fix (`.sc-shape` ↔ `.sc-check` based on `.active`):
 *
 *   container:   flex gap-[5px]
 *   chip:        flex-1 items-center justify-center gap-1 py-[6px]
 *                rounded-pill text-[11px] font-bold tracking-[0.01em]
 *                border-[1.5px], styling per-status (see PAINT above)
 *   icons:       inactive chip → per-status shape (▾ ● ▴)
 *                active chip   → check (✓)
 *
 * Replaces the shared FilterPills component for this context — the
 * homepage uses single-style pill toggles; the map needs the per-status
 * tinted-vs-filled treatment which doesn't fit FilterPills' API.
 * FilterPills stays in shared/ unchanged for the rankings page.
 */
export function StatusChips({ selected, onToggle }: StatusChipsProps) {
  // 2026-05-18 feedback: no 'balanced' chip — the report doesn't
  // argue for that classification, so we don't surface it as a filter.
  const statuses: ReadonlyArray<AlignmentStatus> = ['shortage', 'surplus'];
  return (
    <div role="group" aria-label="Filter by alignment status" className="flex gap-[5px]">
      {statuses.map((status) => {
        const isActive = selected.has(status);
        return (
          <button
            key={status}
            type="button"
            aria-pressed={isActive}
            onClick={() => onToggle(status)}
            className={cn(
              'flex flex-1 items-center justify-center gap-1 rounded-pill border-[1.5px] py-[6px] text-[11px] font-bold tracking-[0.01em] transition',
              isActive ? PAINT[status].active : PAINT[status].inactive,
            )}
          >
            {isActive ? <CheckIcon /> : <ShapeIcon status={status} />}
            {LABEL[status]}
          </button>
        );
      })}
    </div>
  );
}

